package com.techspiration.webuserapp.api

import com.techspiration.core.contract.ApiRoutes
import com.techspiration.core.contract.PublicListingStatsResponse
import com.techspiration.core.contract.contractJson
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.dom.HTMLMetaElement
import org.w3c.fetch.RequestInit

/**
 * Tanıtım sitesinin backend istemcisi — yalnız **auth'suz public** uçlar.
 *
 * Bilerek dar tutulmuştur: bu bundle herkese açık servis edilir, dolayısıyla içinde hiçbir anahtar
 * (`X-Web-Key`, admin token…) bulunmaz ve korumalı uç çağrılmaz.
 *
 * **Neden Ktor client değil, tarayıcının kendi `fetch`'i?** `:webAdminApp` Ktor kullanır çünkü
 * onlarca uç + auth başlığı + yeniden deneme yönetir; burada TEK bir GET var. Ktor client +
 * ContentNegotiation eklemek üretim bundle'ını ~610 KB'den ~1.25 MB'a çıkarıyordu — halka açık
 * bir landing sayfası için ödenmeyecek bir bedel. Route sabiti ve DTO yine `core:contract`'tan
 * gelir, yani sunucu ile tek kaynak paylaşımı (asıl önemli olan) korunur.
 */
class PublicApi(
    private val baseUrl: String = defaultBaseUrl,
) {
    /**
     * Ana sayfadaki üçlü sayaç. Ağ hatası, HTTP hata kodu veya bozuk gövde durumunda exception
     * fırlatır — çağıran ([com.techspiration.webuserapp.home.ListingStatsSection]) hata durumunu
     * gösterir, sıfır GÖSTERMEZ.
     */
    suspend fun listingStats(): PublicListingStatsResponse {
        val response = window
            .fetch("$baseUrl${ApiRoutes.PUBLIC_LISTING_STATS}", RequestInit(method = "GET"))
            .await()
        check(response.ok) { "İlan sayıları alınamadı: HTTP ${response.status}" }
        return contractJson.decodeFromString(response.text().await())
    }
}

/**
 * Backend kökü. Öncelik sırası:
 *
 * 1. `index.html`'deki `<meta name="kamuapp-api-base" content="...">` — doluysa aynen kullanılır.
 *    Site ileride API'den FARKLI bir origin'e taşınırsa tek dokunulacak yer burasıdır; bundle
 *    yeniden derlenmez. Normalde BOŞ bırakılır.
 * 2. Sayfa **webpack dev server**'dan geliyorsa (port [DEV_SERVER_PORT]) → `http://localhost:8080`.
 *    Yalnız bu durumda site ile backend ayrı origin'dedir; CORS izni `Cors.kt` dev fallback'inde.
 * 3. Diğer tüm hâller → **boş string, yani göreli yol** (`/api/v1/...`). Hem prod'da (site ve API
 *    aynı origin'den, Ktor `staticSiteRoutes`) hem de `:server:run` ile lokalde bundle'ı test
 *    ederken doğrudur — sabit `localhost:8080` yazsaydık server farklı bir PORT ile koştuğunda
 *    (test/çakışma) istemci yanlış adrese giderdi.
 */
internal val defaultBaseUrl: String
    get() {
        val override = (document.querySelector("meta[name='kamuapp-api-base']") as? HTMLMetaElement)
            ?.content
            ?.trim()
            ?.takeIf { it.isNotEmpty() }
        if (override != null) return override.trimEnd('/')

        val location = window.location
        val isDevServer = (location.hostname == "localhost" || location.hostname == "127.0.0.1") &&
            location.port == DEV_SERVER_PORT
        return if (isDevServer) "http://localhost:8080" else ""
    }

/** `webUserApp/build.gradle.kts` içindeki webpack `devServer.port` ile AYNI olmalı. */
private const val DEV_SERVER_PORT = "8082"
